import React, { FC } from 'react'
import { Col, Row, Tag, Typography } from 'antd'
import { ExpandingCard } from '../../../../../Components/UI'
import { formatCost } from '../../../../../Utilities'
import { s } from './style'
import {
  BookingEnquiryDetailsEventFragment,
  useGetEnquiryDetailsEventTypeQuery,
} from './EnquiryDetails.generated-types'
import { useDateTimeDuration } from '../../../../../Hooks'

const { Text } = Typography

// These are stored in the backend (as strings) under the event but they are defined here to ensure type safety
type LayoutKey =
  | 'boardroom'
  | 'cabaret'
  | 'u-shaped'
  | 'theatre'
  | 'standing'
  | 'classroom'
  | 'not_sure'

// The ids are stored in the back-end but the labels are not stored anywhere, so instead they are mapped here
const LAYOUT_IDS_TO_LAYOUT_LABELS: Record<LayoutKey, string> = {
  boardroom: 'boardroom',
  cabaret: 'cabaret',
  'u-shaped': 'u-shaped',
  theatre: 'theatre',
  standing: 'standing',
  classroom: 'classroom',
  not_sure: 'not sure',
}

const TAG_STYLE: React.CSSProperties = {
  padding: '6px 12px',
}

interface EnquiryDetailsProps {
  defaultExpanded: boolean
  event: BookingEnquiryDetailsEventFragment
}

interface ColumnItemProps {
  headingText: string
  body: string | JSX.Element
}

const ColumnItem = ({ headingText, body }: ColumnItemProps): JSX.Element => (
  <>
    <Typography style={{ color: '#595959', marginBottom: '4px' }}>
      {headingText}
    </Typography>
    {typeof body === 'string' ? (
      <Typography style={{ color: '#262626' }}>{body}</Typography>
    ) : (
      body
    )}
  </>
)

export const EnquiryDetails: FC<EnquiryDetailsProps> = ({
  defaultExpanded,
  event,
}): JSX.Element => {
  const {
    eventType,
    budget,
    currency,
    attendees,
    location,
    spaceFacilities,
    cateringNeeds: cateringRequests,
    additionalNotes,
    layoutID,
    styles,
    radius,
    flexibleWithDates,
    flexibleWithDatesNote,
  } = event

  const { durationFullDate, durationRange } = useDateTimeDuration(event)

  // TODO: remove this when the event type is correctly stitched and available on the event itself.
  const [{ fetching: fetchingEventEventTypeData, data: eventEventTypeData }] =
    useGetEnquiryDetailsEventTypeQuery({
      variables: { id: eventType.id },
    })

  return (
    <div style={s.root}>
      <ExpandingCard
        title={
          <Text strong style={{ color: '#000000', fontSize: '16px' }}>
            Enquiry details
          </Text>
        }
        defaultExpanded={defaultExpanded}
      >
        <Row gutter={[32, 32]}>
          <Col span={12}>
            <ColumnItem
              headingText="Preferred location"
              body={`${location}${radius ? ` + ${radius}km` : ''}`}
            />
          </Col>
          <Col span={12}>
            <ColumnItem
              headingText="Timings"
              body={
                <>
                  <div>
                    <Typography>{durationFullDate}</Typography>
                  </div>
                  <div>
                    <Typography>{durationRange}</Typography>
                  </div>
                </>
              }
            />
          </Col>
          <Col span={12}>
            <Row gutter={32} wrap={false}>
              {!fetchingEventEventTypeData && eventEventTypeData ? (
                <Col style={{ overflow: 'hidden', flexGrow: '1' }}>
                  <ColumnItem
                    headingText="Type"
                    body={eventEventTypeData?.collection.name || null}
                  />
                </Col>
              ) : null}
              <Col style={{ flexGrow: '1' }}>
                <ColumnItem
                  headingText="Budget"
                  body={formatCost(currency, budget)}
                />
              </Col>
              <Col style={{ flexGrow: '1' }}>
                <ColumnItem
                  headingText="Attendees"
                  body={
                    <Typography>
                      {attendees} {LAYOUT_IDS_TO_LAYOUT_LABELS[layoutID]}
                    </Typography>
                  }
                />
              </Col>
            </Row>
          </Col>
          {flexibleWithDates ? (
            <Col span={12}>
              {
                // It is possible to be flexible with dates but have left no note - handle this case specially
              }
              <ColumnItem
                headingText="Flexible?"
                body={flexibleWithDatesNote || 'Yes'}
              />
            </Col>
          ) : null}
          {spaceFacilities?.length ? (
            <Col span={12}>
              <ColumnItem
                headingText="Facilities"
                body={
                  <div
                    style={{
                      marginBottom: 24,
                      display: 'flex',
                      flexWrap: 'wrap',
                      rowGap: '8px',
                    }}
                  >
                    {spaceFacilities.map(({ name }) => (
                      <Tag key={name} style={TAG_STYLE}>
                        {name}
                      </Tag>
                    ))}
                  </div>
                }
              />
            </Col>
          ) : null}
          {styles?.length ? (
            <Col span={12}>
              <ColumnItem
                headingText="Venue style"
                body={
                  <div>
                    {styles.map(({ id, label }) =>
                      // These should probably not be nullable but if for some reason they are, it is better to show nothing than to crash the application
                      id && label ? (
                        <Tag key={id} style={TAG_STYLE}>
                          {label}
                        </Tag>
                      ) : null
                    )}
                  </div>
                }
              />
            </Col>
          ) : null}{' '}
          {
            // Catering requests can be saved as an empty string - one still wants to hide the column if this is true
            cateringRequests && cateringRequests !== '' ? (
              <Col span={12}>
                <ColumnItem
                  headingText="Catering requests"
                  body={`"${cateringRequests}"`}
                />
              </Col>
            ) : null
          }
          {
            // Additional can be saved as an empty string - one still wants to hide the column if this is true
            additionalNotes && additionalNotes !== '' ? (
              <Col span={12}>
                <ColumnItem
                  headingText="Additional notes"
                  body={`"${additionalNotes}"`}
                />
              </Col>
            ) : null
          }
        </Row>
      </ExpandingCard>
    </div>
  )
}
